import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Download, Share2, ArrowRight, Copy } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, errorMessage } from "@/lib/api";

export default function CertificatesPage() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [certs, setCerts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/certificates");
        setCerts(data);
      } catch (e) { setError(errorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const copy = async (id) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/verify/${id}`);
      setCopied(id);
      setTimeout(() => setCopied(""), 1800);
    } catch (_e) {}
  };

  return (
    <AppShell section="user">
      <div className="p-6 lg:p-10 max-w-5xl">
        <div className="overline mb-3">Achievements</div>
        <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tight leading-tight mb-2">My certificates</h1>
        <p className="text-[14.5px] text-muted-foreground mb-10 max-w-2xl">
          Every time you complete a training track, we issue a verifiable PDF certificate. Download it, share the verification link, or attach it to your resume.
        </p>

        {error && <div className="mb-5 p-3 border border-destructive/30 bg-destructive/5 text-destructive text-[13px]">{error}</div>}

        {loading ? (
          <div className="overline">Loading...</div>
        ) : certs.length === 0 ? (
          <div className="border border-border p-12 text-center bg-white" data-testid="certs-empty">
            <Award className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
            <div className="font-display font-bold mt-3">No certificates yet</div>
            <p className="text-[13.5px] text-muted-foreground mt-2 max-w-md mx-auto">
              Complete one of your assigned modules and click "Mark as complete" to earn your first certificate.
            </p>
            <Link to="/dashboard" className="mt-6 h-10 px-5 inline-flex items-center bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-[#001d6b]">
              Go to my learning <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Link>
          </div>
        ) : (
          <div className="border border-border bg-white">
            {certs.map((c, i) => (
              <div key={c.cert_id} className={`p-6 lg:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5 ${i !== certs.length - 1 ? "border-b border-border" : ""}`}>
                <div className="flex items-start gap-4 min-w-0">
                  <div className="h-12 w-12 shrink-0 bg-primary/10 text-primary inline-flex items-center justify-center"><Award className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <div className="overline mb-1">Issued {c.issued_at?.slice(0, 10)}</div>
                    <div className="font-display font-black text-lg lg:text-xl tracking-tight truncate">{c.module_name}</div>
                    <div className="text-[12.5px] text-muted-foreground mt-1 font-mono truncate">{c.cert_id}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <a data-testid={`cert-download-${c.cert_id}`} href={`${BACKEND}/api/certificates/${c.cert_id}/download`} target="_blank" rel="noreferrer"
                     className="h-10 px-4 inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold text-[12.5px] hover:bg-[#001d6b]">
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                  <button data-testid={`cert-copy-${c.cert_id}`} onClick={() => copy(c.cert_id)}
                          className="h-10 px-4 inline-flex items-center gap-2 border border-border font-semibold text-[12.5px] hover:bg-accent">
                    {copied === c.cert_id ? <><Copy className="h-3.5 w-3.5" /> Copied!</> : <><Share2 className="h-3.5 w-3.5" /> Share link</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
